import { WatchQueryFetchPolicy } from '@apollo/client';
import { useShouldRemoveFileUploadPrefix } from '@modules/common/hooks';
import { getReportDataPointFromAggregatesResponse } from '@modules/common/utils';
import { useExecuteQuantifyChartEql } from '@modules/quantify/hooks/useFetchQuantifyChartData/useExecuteQuantifyChartEql';
import { useGetQuantifyFormulaDisplayName } from '@modules/quantify/hooks/useGetQuantifyFormulaDisplayName';
import { useIsSingleRecordQuery } from '@modules/quantify/hooks/useIsSingleRecordQuery';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getCompareDataPointsFromAggregatesResponse,
  isValidQuery,
} from '@modules/quantify/utils';
import { useCallback, useRef } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { shallow } from 'zustand/shallow';

interface useFetchQuantifyChartDataParams {
  fetchPolicy?: WatchQueryFetchPolicy;
  abortSignal?: AbortSignal;
}

export function useFetchQuantifyChartData(
  options?: useFetchQuantifyChartDataParams,
) {
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isSingleRecordQuery = useIsSingleRecordQuery();
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const [lazyQuery, { status, error }] = useExecuteQuantifyChartEql({
    fetchPolicy: 'no-cache',
    abortSignal: options?.abortSignal,
  });
  const { formulaDisplayName } = useGetQuantifyFormulaDisplayName({
    shouldIncludeGroupByName: true,
  });

  const quantifyState = useQuantifyStore(({ query }) => {
    return {
      visibleDataPoints: query.visibleDataPoints,
      shouldShowTopDataPoints: query.shouldShowTopDataPoints,
      formulaID: query.formulaQuery?.eqlFunction,
      measuredAsCriteria: query.formulaQuery?.criteria,
      query: query,
    };
  }, shallow);

  const dependencies = {
    ...quantifyState,
    formulaDisplayName,
    isSingleRecordQuery,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const fetchQuantifyData = useCallback(async () => {
    const isMeasuredAsCriteriaRequiredButNotPresent =
      !dependenciesRef.current.measuredAsCriteria;

    if (
      !isValidQuery(dependenciesRef.current.query) ||
      isMeasuredAsCriteriaRequiredButNotPresent
    ) {
      dispatch({
        type: QuantifyActionType.SET_REPORT_DATA,
        payload: {
          quantify: { chartDataPoints: null, aggregateFormulaResponse: null },
          compare: { chartDataPoints: null, aggregateFormulaResponse: null },
        },
      });
      return;
    }

    if (
      !dependenciesRef.current.shouldShowTopDataPoints &&
      !dependenciesRef.current.visibleDataPoints?.length
    ) {
      dispatch({
        type: QuantifyActionType.SET_REPORT_DATA,
        payload: {
          quantify: { chartDataPoints: [] },
          compare: { chartDataPoints: [] },
        },
      });
      return;
    }

    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: { isChartQueryInProgress: true },
    });
    dispatch({
      type: QuantifyActionType.SET_REPORT_DATA,
      payload: {
        quantify: { chartDataPoints: [] },
        compare: { chartDataPoints: [] },
      },
    });

    try {
      const resp = await lazyQuery();
      const result = resp?.data?.executeEQL?.result;

      if (result?.__typename === 'GetAggregatesResponse' && result.data) {
        const chartDataPoints = getReportDataPointFromAggregatesResponse({
          shouldRemoveFileUploadPrefix,
          aggregatesResponseView: result,
          whitelistIDs: dependenciesRef.current.shouldShowTopDataPoints
            ? undefined
            : dependenciesRef.current.visibleDataPoints,
        });

        if (dependenciesRef.current.isSingleRecordQuery) {
          chartDataPoints.forEach((dataPoint) => {
            dataPoint.label = dependenciesRef.current.formulaDisplayName ?? '';
          });
        }

        dispatch({
          type: QuantifyActionType.SET_REPORT_DATA,
          payload: {
            quantify: { chartDataPoints, valueRange: result.valueRange },
          },
        });

        dispatch({
          type: QuantifyActionType.SET_VISUAL,
          payload: { totalAggregateHits: result.aggregateHits },
        });
      }

      if (result?.__typename === 'GetDeltaAggregatesResponse') {
        const { records } = getCompareDataPointsFromAggregatesResponse({
          shouldRemoveFileUploadPrefix,
          deltaAggregatesResponseView: result,
          whitelistIDs: dependenciesRef.current.shouldShowTopDataPoints
            ? undefined
            : dependenciesRef.current.visibleDataPoints,
        });

        if (dependenciesRef.current.isSingleRecordQuery) {
          records.forEach((record) => {
            record.label = dependenciesRef.current.formulaDisplayName ?? '';
          });
        }

        dispatch({
          type: QuantifyActionType.SET_REPORT_DATA,
          payload: {
            compare: {
              chartDataPoints: records,
              valueRange: result.valueRange,
            },
          },
        });

        dispatch({
          type: QuantifyActionType.SET_VISUAL,
          payload: { totalAggregateHits: result.aggregateHits },
        });
      }
    } catch {
    } finally {
      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: { isChartQueryInProgress: false },
      });
    }
  }, [lazyQuery, dispatch, shouldRemoveFileUploadPrefix]);

  return [fetchQuantifyData, { isLoading: IsLoading(status), error }] as const;
}
